'use client';
import { createTheme, type ThemeOptions } from '@mui/material/styles';
import siteConfig from '@/config/site';
import { colors } from './tokens';

/**
 * Le thème de « la vitrine d’herboriste » (docs/site-design.md).
 *
 * Deux voix typographiques, comme l’enseigne : **Marcellus** pour le nom et les
 * grands titres (romaine lapidaire, fûts évasés, C large et ouvert, un seul poids
 * — la hiérarchie se fait par la taille et l’interlettrage, jamais par le gras),
 * **Mulish** pour tout le reste.
 *
 * Forme : rayon 2 px, aucune ombre nulle part, filets d’un pixel. L’interaction
 * est un soulignement argile tracé depuis la gauche — pas de lift, pas d’ombre,
 * pas de mise à l’échelle.
 */

const DISPLAY = 'var(--font-marcellus), Georgia, "Times New Roman", serif';
const BODY = 'var(--font-mulish), system-ui, -apple-system, sans-serif';

const MOBILE = '@media (max-width:599.95px)';

/** Le filet argile de 1,5 px qui se trace depuis la gauche, en 180 ms. */
function underline(color: string) {
  return {
    '&::after': {
      content: '""',
      position: 'absolute' as const,
      left: 0,
      right: 0,
      bottom: 0,
      height: '1.5px',
      backgroundColor: color,
      transform: 'scaleX(0)',
      transformOrigin: 'left center',
      transition: 'transform 180ms ease',
    },
    '&:hover::after, &:focus-visible::after': { transform: 'scaleX(1)' },
    '@media (prefers-reduced-motion: reduce)': {
      '&::after': { transition: 'none' },
    },
  };
}

const base: ThemeOptions = {
  palette: {
    mode: siteConfig.brand.mode,
    primary: { main: siteConfig.brand.primary, contrastText: colors.cream },
    secondary: { main: siteConfig.brand.secondary, contrastText: colors.cream },
    // `warning` ne sert qu’aux étoiles de <MapRating> : l’ambre de leurs
    // appliques, assombrie pour tenir le contraste sur le crème.
    warning: { main: colors.amberDeep },
    background: { default: colors.cream, paper: colors.creamDeep },
    text: { primary: colors.ink, secondary: 'rgba(37, 43, 34, 0.72)' },
    divider: colors.hairline,
  },
  // Presque net — les menuiseries de la boutique sont droites.
  shape: { borderRadius: 2 },
  // Aucune ombre, nulle part : la séparation vient du ton, pas de l’élévation.
  shadows: Array(25).fill('none') as ThemeOptions['shadows'],
  typography: {
    fontFamily: BODY,
    fontSize: 16,
    // Échelle ratio 1.33.
    h1: {
      fontFamily: DISPLAY,
      fontWeight: 400,
      fontSize: '2.75rem',
      lineHeight: 1.12,
      letterSpacing: '0.04em',
      [MOBILE]: { fontSize: '2rem' },
    },
    h2: {
      fontFamily: DISPLAY,
      fontWeight: 400,
      fontSize: '2rem',
      lineHeight: 1.12,
      letterSpacing: '0.04em',
      [MOBILE]: { fontSize: '1.625rem' },
    },
    // h3 en Mulish 600 : les titres courts de sous-section ont besoin d’un poids
    // que la romaine n’a pas (Marcellus n’existe qu’en 400).
    h3: { fontFamily: BODY, fontWeight: 600, fontSize: '1.375rem', lineHeight: 1.3 },
    h4: { fontFamily: BODY, fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.35 },
    h5: { fontFamily: BODY, fontWeight: 600, fontSize: '1rem', lineHeight: 1.4 },
    h6: { fontFamily: BODY, fontWeight: 600, fontSize: '0.9375rem', lineHeight: 1.4 },
    body1: { fontSize: '1rem', lineHeight: 1.65 },
    body2: { fontSize: '0.875rem', lineHeight: 1.65 },
    caption: { fontSize: '0.875rem', lineHeight: 1.6 },
    button: { fontFamily: BODY, textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
  },
  components: {
    // Les sous-titres ne doivent pas devenir des <h6> : ils casseraient l’ordre
    // des niveaux de titre (docs/design.md §8).
    MuiTypography: {
      defaultProps: { variantMapping: { subtitle1: 'p', subtitle2: 'p' } },
    },
    MuiCssBaseline: {
      styleOverrides: {
        // Un état de focus toujours visible (docs/design.md §8). Les bandes
        // sombres redéfinissent la couleur localement.
        'a:focus-visible, button:focus-visible, [tabindex]:focus-visible': {
          outline: `2px solid ${colors.ink}`,
          outlineOffset: '2px',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: true },
      styleOverrides: {
        root: {
          position: 'relative',
          borderRadius: 2,
          paddingTop: 12,
          paddingBottom: 12,
          paddingLeft: 24,
          paddingRight: 24,
          minHeight: 48,
          transition: 'none',
        },
        // Bouton plein (l’appel) : le filet se trace en crème sur l’argile.
        contained: {
          ...underline(colors.cream),
          '&:hover': { backgroundColor: siteConfig.brand.secondary },
        },
        outlined: { borderColor: colors.hairline, ...underline(colors.clay) },
        text: { ...underline(colors.clay) },
      },
    },
    MuiLink: {
      defaultProps: { underline: 'none' },
      styleOverrides: {
        root: {
          position: 'relative',
          display: 'inline-block',
          color: 'inherit',
          ...underline(colors.clay),
        },
      },
    },
    MuiCard: { styleOverrides: { root: { borderRadius: 2, backgroundImage: 'none' } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    /**
     * Étiquettes **au-dessus** du champ, pas flottantes dans son bord.
     *
     * L’encoche de MUI oblige l’étiquette à chevaucher le contour, ce qui ne
     * marche qu’avec un champ transparent : dès que le champ a un fond opaque —
     * le nôtre est crème — l’étiquette se fait repeindre par-dessus, et la
     * rattraper demande une pastille de fond sous chaque libellé. On sort du
     * problème plutôt que de le rustiner : l’étiquette redevient un bloc normal
     * posé au-dessus du champ, et l’encoche est refermée.
     */
    MuiTextField: { defaultProps: { InputLabelProps: { shrink: true } } },
    MuiInputLabel: {
      styleOverrides: {
        outlined: {
          position: 'relative',
          transform: 'none',
          maxWidth: '100%',
          fontSize: '0.875rem',
          fontWeight: 600,
          lineHeight: 1.4,
          marginBottom: 6,
          color: colors.ink,
          '&.Mui-focused': { color: colors.green },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 2, backgroundColor: colors.cream },
        // `top: 0` + légende masquée : le contour redevient un rectangle plein,
        // sans le décrochement que l’encoche réservait à l’étiquette.
        notchedOutline: {
          borderColor: colors.hairline,
          top: 0,
          '& legend': { display: 'none' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 2, height: 40, fontSize: '0.9375rem' },
        outlined: { borderColor: colors.hairline },
      },
    },
    MuiAlert: { styleOverrides: { root: { borderRadius: 2 } } },
    MuiAppBar: { defaultProps: { elevation: 0, color: 'inherit' } },
  },
};

export const theme = createTheme(base);
export default theme;
